import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  Col,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Statistic,
} from 'antd';
import { quantityFormatter } from '@/utils/number';
import { useDefaultEmptyFieldError, useWindowSize } from '@/hooks';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import SupplierSelect from '@/components/Suppliers/SupplierSelect';
import { useMemo } from 'react';
import {
  WorkOrderItem,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import Authorities from '@/types/Authority';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import formatterPrice from '@/utils/number/formatterPrice';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import formatterPercent from '@/utils/number/formatterPercent';
import { useWorkorderItemPermissions } from './workorderItemPermissions';
import fieldRules from '@/utils/functions/fieldRules';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { useWorkorderItems } from '../WorkOrderItemModal';
import { useCustomerGeneral } from '@/core/domain/people/Customers/Customers.hooks';
import { isAppPortocar } from '@/index';
import {
  getBenefitMaxQuantity,
  getBenefitQuantityFractions,
  isQuantityForbiddenDecimal,
  isQuantityOverLimit,
} from '../../Complements/ComplementPolicyBenefits/constants';
import WorkOrderItemPortoBatteryLabelCode from '../WorkOrderItemList/WorkOrderItemPortoBatteryLabelCode';
import TextArea from 'antd/lib/input/TextArea';

const defaultStatistcProps = {
  decimalSeparator: ',',
  groupSeparator: '.',
};

export interface WorkOrderItemFormData {
  itemName?: string;
  manufacturingCode?: string;
  subAreaId?: number;
  manufacturerId?: number;
  supplierId: number;
  isInternal: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountValue: number;
  unitCost: number;
  isPaintedPart: string;
  time?: number;
  type: WorkOrderItemType;
  configDiscount?: number;
  employeeId?: number;
}

interface Props {
  isEdit?: boolean;
  currentStep?: number;
  workorderItem?: WorkOrderItem;
  form: FormInstance<WorkOrderItemFormData>;
  onValuesChange?: (field: unknown, values: WorkOrderItemFormData) => void;
}

function WorkOrderItemPartForm({
  form,
  isEdit,
  workorderItem,
  onValuesChange,
  currentStep,
}: Props) {
  const { items } = useWorkorderItems();

  const currentItem =
    typeof currentStep === 'number' ? items[currentStep] : workorderItem;

  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const quantity = Form.useWatch(['quantity'], form);
  const unitPrice = Form.useWatch(['unitPrice'], form);
  const discountValue = Form.useWatch(['discountValue'], form);
  const unitCost = Form.useWatch(['unitCost'], form);
  const discount = Form.useWatch(['discount'], form);

  const itemPermissions = useWorkorderItemPermissions();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const hasPolicyAndBenefit =
    !!workorderItem?.benefitId && !!complement?.policyNumber;

  const benefitServiceCodeNumber = workorderItem?.benefitServiceCode
    ? Number(workorderItem.benefitServiceCode)
    : undefined;
  const isBenefitItem =
    isAppPortocar &&
    typeof benefitServiceCodeNumber === 'number' &&
    !Number.isNaN(benefitServiceCodeNumber);
  const parentBenefit = isBenefitItem
    ? { serviceCode: benefitServiceCodeNumber }
    : undefined;
  const benefitMaxQuantity = isBenefitItem
    ? getBenefitMaxQuantity(parentBenefit)
    : undefined;
  const benefitFractions = isBenefitItem
    ? getBenefitQuantityFractions(parentBenefit)
    : undefined;

  const companyWorkorderConfig = useWorkorderConfigurationCompanyQuery(
    workorder?.company.id,
  );
  const companyConfigData = companyWorkorderConfig.data;

  const partStockPrices = usePartStockPrices(
    currentItem?.itemId,
    !!currentItem?.itemId,
  );

  const stockPrices = useMemo(
    () =>
      partStockPrices.data?.result.find(
        (part) => part.company.id === workorder?.company.id,
      ),
    [workorder?.company.id, partStockPrices, partStockPrices.dataUpdatedAt],
  );

  const { data: customer } = useCustomerGeneral(workorder?.customer?.id);

  const configDiscountField =
    (stockPrices?.maxDiscount || companyConfigData?.maxPartDiscount || 0) * 100;

  const subTotal = (quantity || 0) * (unitPrice || 0);

  const amount = subTotal - discountValue;

  const hasDiscountCompanyLimit =
    !itemPermissions.addDiscountGreaterDefinedByCompany;

  const hasDiscounStocktLimit = !itemPermissions.giveDiscountOverLimit;

  const hasConfigDiscount =
    companyWorkorderConfig.isFetched &&
    partStockPrices.isFetched &&
    !!configDiscountField;

  const hasInvalidQuantity =
    quantity < (workorderItem?.status?.done || 0) &&
    !companyConfigData?.isAutomaticStock &&
    isEdit;

  const hasLowValuePermission = hasPermissions([
    Authorities.MOVIMENTATION_WORK_ORDER_ALLOW_TO_ENTER_ITEM_SALES_VALUE_BELOW_PROFIT_MARGIN,
  ]);

  const totalValue = useMemo(() => {
    const appliedDiscount = discountValue || (subTotal * (discount || 0)) / 100;
    return subTotal - appliedDiscount;
  }, [subTotal, discount, discountValue]);

  const showPermissionAlert = useMemo(() => {
    if (hasLowValuePermission) return false;

    const minSellPrice = stockPrices?.sellPrice || 0;

    return totalValue === 0 || totalValue < minSellPrice;
  }, [hasLowValuePermission, stockPrices?.sellPrice, totalValue /*, partId*/]);

  const validateProfitMargin = useMemo(() => {
    return () => {
      if (hasLowValuePermission) return Promise.resolve();

      const minSellPrice = stockPrices?.sellPrice || 0;

      if (
        totalValue === 0 ||
        (totalValue > 0 && minSellPrice > 0 && totalValue < minSellPrice)
      ) {
        return Promise.reject(
          new Error('Valor total abaixo da margem de lucro'),
        );
      }

      return Promise.resolve();
    };
  }, [hasLowValuePermission, stockPrices?.sellPrice, totalValue]);

  return (
    <Form
      form={form}
      onValuesChange={onValuesChange}
      layout={isMobile ? 'vertical' : 'horizontal'}
      preserve={false}
    >
      {!hasConfigDiscount && (
        <Alert
          type="warning"
          description="Não há uma configuração de desconto definida para esta peça no registro de estoque ou na configuração da empresa vinculada ao orçamento."
          style={{ marginBottom: 24 }}
        />
      )}

      {hasInvalidQuantity && (
        <Alert
          type="warning"
          description={`Quantidade tem que ser maior, ou igual, a entregue: ${quantityFormatter(
            workorderItem?.status?.done || 0,
          )}`}
          style={{ marginBottom: 24 }}
        />
      )}

      <Form.Item hidden name="type">
        <Input />
      </Form.Item>

      <Form.Item hidden name="configDiscount" initialValue={0}>
        <Input />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            name="itemName"
            rules={[
              defaultEmptyFieldError,
              { type: 'string', max: 120, message: 'Máximo de 120 caracteres' },
            ]}
            label={intl.formatMessage({
              id: 'workOrders.domain.items.itemName',
            })}
          >
            <Input
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_DESCRIPTION,
                ])
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            name="manufacturerId"
            rules={[defaultEmptyFieldError]}
            label={'Fabricante'}
          >
            <ManufacturerInfiniteSelectField queryFilter={{ active: true }} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            name="manufacturingCode"
            label={intl.formatMessage({
              id: 'workOrders.domain.items.manufacturingCode',
            })}
          >
            <Input disabled />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem name="subAreaId" label="Área">
            <AreasInfiniteSelectField
              queryFilter={{ active: true }}
              subAreaSelected={workorderItem?.subArea}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            name="supplierId"
            rules={[
              {
                required: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_SUPPLIER,
                ]),
              },
            ]}
            label="Fornecedor"
          >
            <SupplierSelect />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <Form.Item
            name="isInternal"
            initialValue="true"
            label={intl.formatMessage({
              id: 'workOrders.domain.items.partProvided',
            })}
            style={{ marginTop: '6px' }}
          >
            <Radio.Group>
              <Radio
                value="false"
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_INCLUDE_SUPPLIED_PARTS,
                  ])
                }
              >
                <FormattedMessage id="commons.options.yes" />
              </Radio>
              <Radio
                value="true"
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_INCLUDE_SUPPLIED_PARTS,
                  ])
                }
              >
                <FormattedMessage id="commons.options.no" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col span={24} sm={12} lg={3}>
          <FloatingFormItem
            name="quantity"
            rules={[
              defaultEmptyFieldError,

              !companyConfigData?.isAutomaticStock && isEdit
                ? {
                    type: 'number',
                    min: workorderItem?.status?.done || 0,
                    message: '',
                  }
                : { type: 'number' },

              ...(isBenefitItem
                ? [
                    {
                      validator: (_: unknown, value: number) => {
                        if (
                          isQuantityForbiddenDecimal(parentBenefit, value)
                        ) {
                          return Promise.reject(
                            new Error(
                              'Materiais de troca de lâmpada não aceitam casas decimais.',
                            ),
                          );
                        }
                        if (isQuantityOverLimit(parentBenefit, value)) {
                          return Promise.reject(
                            new Error(
                              'Quantidade acima do limite permitido para o benefício.',
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
                : []),
            ]}
            label={intl.formatMessage({
              id: 'workOrders.domain.items.quantity',
            })}
            getValueFromEvent={(value) => {
              const discount = form.getFieldValue('discount');
              const unitPrice = form.getFieldValue('unitPrice');

              const discountValue = (value * unitPrice * discount) / 100 || 0;

              form.setFieldsValue({ discountValue });

              return value;
            }}
          >
            <InputDecimal
              disabled={
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_QUANTITY,
                  ])) ||
                (isEdit &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_QUANTITY,
                  ]))
              }
              min={0}
              max={benefitMaxQuantity}
              fractions={benefitFractions}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'workOrders.domain.items.unitPrice',
            })}
            name="unitPrice"
            initialValue={0}
            getValueFromEvent={(value) => {
              const discount = form.getFieldValue('discount');
              const quantity = form.getFieldValue('quantity');

              const discountValue = (value * quantity * discount) / 100 || 0;

              form.setFieldsValue({ discountValue });

              return value;
            }}
            rules={[
              { validator: validateProfitMargin },
              ...fieldRules([
                {
                  enabled: !itemPermissions.allowSellingBellowCostValue,
                  type: 'number',
                  min: unitCost,
                  message: `Valor mínimo de ${formatterPrice(unitCost)}`,
                },
                {
                  enabled:
                    !itemPermissions.makeSaleAboveCreditLimit &&
                    typeof customer?.creditLimit === 'number',
                  type: 'number',
                  max: customer?.creditLimit,
                  message: `Limite de crédido de: ${formatterPrice(
                    customer?.creditLimit || 0,
                  )}`,
                },
              ]),
            ]}
          >
            <InputDecimal
              disabled={
                hasPolicyAndBenefit ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_VALUE,
                  ])) ||
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_PART_EDIT_SALE_VALUE,
                ])
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={4}>
          <FloatingFormItem
            name="discount"
            label={intl.formatMessage({
              id: 'workOrders.domain.items.discount',
            })}
            getValueFromEvent={(value) => {
              const calc = ((subTotal || 0) * (value || 0)) / 100;

              form.setFieldValue('discountValue', calc);

              return value;
            }}
            rules={[
              { validator: validateProfitMargin },
              () => {
                let max: number | undefined = undefined;

                const companyMaxDiscount =
                  companyConfigData?.maxPartDiscount ?? 0;

                if (hasDiscounStocktLimit && !!stockPrices?.maxDiscount) {
                  max = (stockPrices?.maxDiscount || 0) * 100;
                } else if (
                  hasDiscountCompanyLimit &&
                  typeof companyMaxDiscount === 'number'
                ) {
                  max = companyMaxDiscount * 100;
                }

                const message = `Desconto não pode ser maior que ${formatterPercent(
                  (max || 0) / 100,
                )}`;

                return {
                  max,
                  type: 'number',
                  message,
                };
              },
            ]}
          >
            <InputDecimal
              min={0}
              max={100}
              disabled={
                !unitPrice ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !itemPermissions.openedItemChangeDiscount) ||
                !itemPermissions.editDiscountValue
              }
              key={companyConfigData?.company.id}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={5}>
          <FloatingFormItem
            name="discountValue"
            label={intl.formatMessage({
              id: 'workOrders.domain.items.discountValue',
            })}
            rules={[
              { validator: validateProfitMargin },
              () => {
                let max: number | undefined = undefined;
                const companyMaxDiscount =
                  companyConfigData?.maxPartDiscount ?? 0;

                if (hasDiscounStocktLimit && !!stockPrices?.maxDiscount) {
                  max = subTotal * (stockPrices?.maxDiscount || 0);
                } else if (
                  hasDiscountCompanyLimit &&
                  typeof companyMaxDiscount === 'number'
                ) {
                  max = subTotal * companyMaxDiscount;
                }

                const message = `Desconto não pode ser maior que ${formatterPrice(
                  max || 0,
                )}`;

                return {
                  max,
                  type: 'number',
                  message,
                };
              },
            ]}
            getValueFromEvent={(value) => {
              const calc = (100 * (value || 0)) / (subTotal || 0);

              form.setFieldValue('discount', calc || 0);

              return value;
            }}
          >
            <InputDecimal
              min={0}
              max={subTotal}
              disabled={
                !unitPrice ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !itemPermissions.openedItemChangeDiscount) ||
                !itemPermissions.editDiscountValue
              }
            />
          </FloatingFormItem>
        </Col>

        <Col
          span={24}
          md={12}
          lg={6}
          hidden={
            !hasPermissions([
              Authorities.MOVIMENTATION_WORKORDER_VIEW_PART_COST_PRICE,
            ])
          }
        >
          <FloatingFormItem
            name="unitCost"
            label={intl.formatMessage({
              id: 'workOrders.domain.items.unitCost',
            })}
            rules={[
              {
                type: 'number',
                required: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_COST,
                ]),
                min: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_COST,
                ])
                  ? 1
                  : 0,
              },
            ]}
          >
            <InputDecimal
              min={0}
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_EDIT_COST_PRICE,
                ]) ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_COST_PRICE,
                  ]))
              }
              fractions={4}
            />
          </FloatingFormItem>
        </Col>

        {showPermissionAlert && (
          <Col span={24}>
            <Alert
              type="warning"
              description={`Você não tem permissão para lançar valor total de venda do item abaixo da margem de lucro ${formatterPrice(
                stockPrices?.sellPrice || 0,
              )}`}
            />
          </Col>
        )}

        {!isEdit && (
          <>
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="paint"
                label={intl.formatMessage({
                  id: 'workOrders.domain.items.timeToPaint',
                })}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="repair"
                label={intl.formatMessage({
                  id: 'workOrders.domain.items.timeToRepair',
                })}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                name="change"
                label={intl.formatMessage({
                  id: 'workOrders.domain.items.timeToChange',
                })}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            {!currentItem?.kitId && (
              <Col span={24}>
                <FloatingFormItem name="observation" label="Observação">
                  <TextArea disabled rows={2} />
                </FloatingFormItem>
              </Col>
            )}
          </>
        )}

        <Col span={24}>
          <Row gutter={[24, 16]}>
            <Col flex={1}>
              {unitCost > amount &&
                !itemPermissions.allowSellingBellowCostValue && (
                  <Alert
                    type="warning"
                    description="O preço de venda deve ser maior que o custo unitário"
                  />
                )}
            </Col>

            <Col>
              <Statistic
                {...defaultStatistcProps}
                title={
                  <FormattedMessage id="workOrders.details.items.partTotal" />
                }
                value={quantityFormatter(amount)}
                prefix="R$"
                valueStyle={{
                  color:
                    unitCost > amount &&
                    !itemPermissions.allowSellingBellowCostValue
                      ? 'red'
                      : '#364068',
                }}
              />
            </Col>
          </Row>
        </Col>

        {isAppPortocar && !!workorderItem?.portoBatteryLabelCode && (
          <Col span={24}>
            <WorkOrderItemPortoBatteryLabelCode
              portoBatteryLabelCode={workorderItem.portoBatteryLabelCode}
            />
          </Col>
        )}
      </Row>
    </Form>
  );
}

export default WorkOrderItemPartForm;
